import { API_BASE_URL } from '@synergy/config';
import { Box } from '@mui/material';
import { DocumentStatus, OrderType, getOrderTypeTranslationId } from '@synergy/models';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { PaginatedResponse } from '@synergy/api';
import {
    PaloAmount,
    PaloCancellationNotice,
    PaloColumn,
    PaloDeliveryDateValue,
    PaloFilterChips,
    PaloListView,
    PaloStatusPill,
    usePaloListSort,
} from '@synergy/components-palo';
import { ReactElement, useEffect, useState } from 'react';
import { UrlFilter, ValueOrNDash } from '@synergy/components';
import { isRecentCancellation, portalOrderStatus, portalOrderStatusTranslationId } from '@synergy/utils';
import { useSearchParams } from 'react-router-dom';
import urlJoin from 'url-join';

import { MEMBER_LIST_LIMIT } from '../../../common/utils/member-order-params';
import { MemberPurchaseOrder } from '../models/member-purchase-order';
import { PaloOrderDetails } from './palo-order-details';
import { PaloOrderInvoices } from './palo-order-invoices';
import { PaloOrderLimitLine } from './palo-order-limit-line';
import { PaloOrdersFilter } from './palo-orders-filter';
import { useAddToShopCart } from './use-add-to-shop-cart';
import { useOwnContactFilterNames } from '../../../common/utils/use-own-contact-filter-names';
import { useSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

const isCancelled = (order: MemberPurchaseOrder): boolean => DocumentStatus.Canceled === order.status;

const pdfHref = (order: MemberPurchaseOrder): string =>
    urlJoin(API_BASE_URL, `/purchase-orders/${order.id}/pdf-purchase-order`);

const EMPTY: PaginatedResponse<MemberPurchaseOrder> = { items: [], total: 0, page: 1, limit: 50 };

/**
 * The member's orders in the PALO portal, one row per supplier order (#2279).
 * The shop's two tabs showed the same orders twice, as cards and as a list,
 * and neither said which were cancelled.
 */
export const PaloOrdersOverview = (): ReactElement => {
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    const { sort, params: sortedParams } = usePaloListSort('purchase-orders');
    const currency = useSelector(state => state.currency);
    const ownContactNames = useOwnContactFilterNames();
    const [orders, setOrders] = useState<PaginatedResponse<MemberPurchaseOrder>>(EMPTY);
    const [requesting, setRequesting] = useState(true);
    const [cancellations, setCancellations] = useState<MemberPurchaseOrder[]>([]);
    const [expandedId, setExpandedId] = useState<number>();
    const { add } = useAddToShopCart();

    const page = Number(searchParams.get('page') ?? 1);
    const limit = Number(searchParams.get('limit') ?? MEMBER_LIST_LIMIT);

    useEffect(() => {
        let cancelled = false;
        setRequesting(true);

        void dispatch(async (_dispatch, _getState, { api }) => {
            const result = await api.orders.cget(sortedParams);

            if (!cancelled) {
                setOrders(result.success ? result.result : EMPTY);
                setRequesting(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [dispatch, sortedParams]);

    // Asked for on their own, so one further down the list still warns.
    useEffect(() => {
        void dispatch(async (_dispatch, _getState, { api }) => {
            const result = await api.orders.cget(
                new URLSearchParams({ statuses: DocumentStatus.Canceled, sort: 'number', order: 'desc', limit: '30' })
            );
            setCancellations(result.success ? result.result.items.filter(order => isRecentCancellation(order)) : []);
        });
    }, [dispatch]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const reorder = (order: MemberPurchaseOrder): void => {
        void dispatch(async (_dispatch, _getState, { api }) => {
            const detail = await api.orders.get(order.id);

            if (detail.success) {
                await add(detail.result.items);
            }
        });
    };

    const columns: PaloColumn<MemberPurchaseOrder>[] = [
        {
            key: 'number',
            type: 'documentNumber',
            head: <FormattedMessage id="app.table.column.number" />,
            sortKey: 'number',
            cell: order => order.number,
        },
        {
            key: 'orderDate',
            type: 'date',
            head: <FormattedMessage id="app.orders.column.orderDate" />,
            cell: order => (
                <ValueOrNDash
                    value={order.orderDate}
                    valueRender={value => <FormattedDate value={value} year="numeric" month="short" day="2-digit" />}
                    strictEqual={true}
                />
            ),
        },
        {
            key: 'type',
            type: 'flag',
            head: <FormattedMessage id="app.table.column.type" />,
            cell: order => <FormattedMessage id={getOrderTypeTranslationId(order.type as OrderType)} />,
        },
        {
            key: 'supplier',
            type: 'name',
            head: <FormattedMessage id="app.table.column.supplier" />,
            cell: order => order.supplierAccount?.name,
        },
        {
            key: 'customerContact',
            type: 'classification',
            head: <FormattedMessage id="app.orders.column.orderer" />,
            variant: 'muted',
            cell: order => <ValueOrNDash value={order.customerContact?.fullName} />,
        },
        {
            key: 'costCentre',
            // A cost centre is a short code, measured like a number; at the
            // width of a name the row ran past the screen.
            type: 'documentNumber',
            head: <FormattedMessage id="app.orders.column.costCentre" />,
            variant: 'muted',
            cell: order => <ValueOrNDash value={order.costCentre} />,
        },
        {
            key: 'deliveryAddress',
            type: 'classification',
            head: <FormattedMessage id="app.orders.column.deliveryAddress" />,
            variant: 'muted',
            cell: order => (
                <ValueOrNDash
                    value={[order.deliveryAddress?.title, order.deliveryAddress?.city].filter(Boolean).join(', ')}
                />
            ),
        },
        {
            key: 'deliveryDate',
            type: 'deliveryDate',
            head: <FormattedMessage id="app.orders.column.deliveryDate" />,
            cell: order => (
                <PaloDeliveryDateValue
                    date={order.deliveryDate}
                    type={order.deliveryDateType}
                    void={isCancelled(order)}
                />
            ),
        },
        {
            key: 'itemCount',
            type: 'count',
            head: <FormattedMessage id="app.orders.column.items" />,
            cell: order => order.itemCount,
        },
        {
            key: 'totalNetPrice',
            type: 'amount',
            currency,
            head: <FormattedMessage id="app.orders.column.net" />,
            cell: order => (
                <Box component="span" sx={{ textDecoration: isCancelled(order) ? 'line-through' : undefined }}>
                    <PaloAmount value={order.totalNetPrice} />
                </Box>
            ),
        },
        {
            key: 'status',
            type: 'status',
            // The portal knows two states, "Bestellt" and "Storniert"; the 180
            // pixels of the status kind are for "Beendet ohne Folgebeleg".
            width: '120px',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: order => (
                <PaloStatusPill
                    status={portalOrderStatus(order.status)}
                    label={<FormattedMessage id={portalOrderStatusTranslationId(order.status)} />}
                />
            ),
        },
        {
            key: 'invoice',
            type: 'classification',
            head: <FormattedMessage id="app.orders.detail.invoice" />,
            variant: 'muted',
            cell: order => <PaloOrderInvoices order={order} compact={true} />,
        },
    ];

    // What the page shows adds up without the cancelled orders, which were
    // never delivered (#2279, point 9).
    const counted = orders.items.filter(order => !isCancelled(order));

    return (
        <UrlFilter>
            <PaloListView<MemberPurchaseOrder, number>
                title={<FormattedMessage id="app.navigation.purchases" />}
                filters={<PaloOrdersFilter />}
                filterChips={<PaloFilterChips names={ownContactNames} />}
                notice={
                    <>
                        <PaloOrderLimitLine currency={currency} />
                        <PaloCancellationNotice
                            cancellations={cancellations}
                            getId={order => order.id}
                            title={
                                <FormattedMessage
                                    id="app.orders.cancelledNotice.title"
                                    values={{ count: cancellations.length }}
                                />
                            }
                            describe={order => (
                                <FormattedMessage
                                    id="app.orders.cancelledNotice.entry"
                                    values={{
                                        number: order.number,
                                        supplier: order.supplierAccount?.name,
                                        items: order.itemCount,
                                        amount: <PaloAmount value={order.totalNetPrice} currency={order.currency} />,
                                        orderer: order.customerContact?.fullName ?? '–',
                                        orderDate: order.orderDate ? (
                                            <FormattedDate
                                                value={order.orderDate}
                                                year="numeric"
                                                month="2-digit"
                                                day="2-digit"
                                            />
                                        ) : (
                                            '–'
                                        ),
                                    }}
                                />
                            )}
                            pdfHref={pdfHref}
                            pdfLabel={<FormattedMessage id="app.orders.cancelledNotice.pdf" />}
                            actions={order => (
                                <a
                                    href="#"
                                    onClick={event => {
                                        event.preventDefault();
                                        reorder(order);
                                    }}
                                >
                                    <FormattedMessage id="app.orders.cancelledNotice.reorder" />
                                </a>
                            )}
                            footer={<FormattedMessage id="app.orders.cancelledNotice.text" />}
                        />
                    </>
                }
                sort={sort}
                onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                searchValue={searchParams.get('search') ?? ''}
                searchPlaceholder={intl.formatMessage({ id: 'app.common.searchPlaceholder' })}
                onSearchChange={value => {
                    const next = new URLSearchParams(searchParams);
                    if (value) {
                        next.set('search', value);
                    } else {
                        next.delete('search');
                    }
                    next.set('page', '1');
                    setSearchParams(next, { replace: true });
                }}
                columns={columns}
                items={orders.items}
                getRowId={order => order.id}
                requesting={requesting}
                isRowCritical={isCancelled}
                rowActions={order => [
                    {
                        label: <FormattedMessage id="app.orders.pdf" />,
                        onClick: () => window.open(pdfHref(order), '_blank', 'noopener'),
                    },
                    {
                        label: <FormattedMessage id="app.orders.reorder" />,
                        onClick: () => reorder(order),
                    },
                ]}
                onRowClick={order => setExpandedId(current => (current === order.id ? undefined : order.id))}
                expandedRowId={expandedId}
                renderExpanded={order => <PaloOrderDetails order={order} />}
                emptyLabel={<FormattedMessage id="app.orders.empty" />}
                pagination={{
                    count: orders.total,
                    page,
                    limit,
                    onPageChange: newPage => updateParams({ page: String(newPage) }),
                    onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                    summary:
                        counted.length > 0 ? (
                            <FormattedMessage
                                id="app.orders.pageSummary"
                                values={{
                                    count: counted.length,
                                    amount: (
                                        <FormattedNumber
                                            value={counted.reduce((sum, order) => sum + order.totalNetPrice, 0)}
                                            style="currency"
                                            currency={currency}
                                        />
                                    ),
                                }}
                            />
                        ) : undefined,
                }}
            />
        </UrlFilter>
    );
};
